"use client";

import React, { createContext, useContext, useRef } from "react";

/**
 * Контекст для синхронизации состояния отмены запросов между экземплярами хука useAgentChat
 * Заменяет использование window объекта для shared state
 */
interface AgentChatCancellationContextValue {
  /** Shared ref для синхронной проверки состояния загрузки */
  sharedIsLoadingRef: React.MutableRefObject<boolean>;
  /** Shared ref для синхронной проверки состояния отмены */
  sharedIsCancellingRef: React.MutableRefObject<boolean>;
  /** Shared ref для AbortController между экземплярами хука */
  sharedAbortControllerRef: React.MutableRefObject<AbortController | null>;
}

const AgentChatCancellationContext = createContext<AgentChatCancellationContextValue | null>(null);

const fallbackSharedIsLoadingRef: React.MutableRefObject<boolean> = { current: false };
const fallbackSharedIsCancellingRef: React.MutableRefObject<boolean> = { current: false };
const fallbackSharedAbortControllerRef: React.MutableRefObject<AbortController | null> = {
  current: null,
};

/**
 * Провайдер контекста для синхронизации состояния отмены запросов
 * Должен быть обернут вокруг компонентов, использующих useAgentChat
 */
export function AgentChatCancellationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // Создаем refs один раз при создании провайдера
  const sharedIsLoadingRef = useRef<boolean>(false);
  const sharedIsCancellingRef = useRef<boolean>(false);
  const sharedAbortControllerRef = useRef<AbortController | null>(null);

  const value: AgentChatCancellationContextValue = {
    sharedIsLoadingRef,
    sharedIsCancellingRef,
    sharedAbortControllerRef,
  };

  return (
    <AgentChatCancellationContext.Provider value={value}>
      {children}
    </AgentChatCancellationContext.Provider>
  );
}

/**
 * Хук для использования контекста отмены запросов
 * Возвращает fallback refs если контекст недоступен (для обратной совместимости)
 */
export function useAgentChatCancellationContext(): AgentChatCancellationContextValue {
  const context = useContext(AgentChatCancellationContext);

  // Fallback на модульные refs если контекст недоступен
  // Это обеспечивает обратную совместимость и избегает утечек в window
  if (!context) {
    return {
      sharedIsLoadingRef: fallbackSharedIsLoadingRef,
      sharedIsCancellingRef: fallbackSharedIsCancellingRef,
      sharedAbortControllerRef: fallbackSharedAbortControllerRef,
    };
  }

  return context;
}
